import { Listbox, Transition } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';
import { FetchMySchoolsQuery, FetchMySchoolsQueryVariables } from '@/graphql/generated/graphql';
import { QUERY_SCHOOL_CHILDREN } from '@/graphql/queries/dashboard/dashboard';
import { useQuery } from '@apollo/client';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AnimatePresence, motion } from 'framer-motion';

type SchoolsFilterProps = {
  selectedSchoolsIds: string[];
  onChange: (schoolIds: string[]) => void;
  showChips?: boolean;
  label?: string;
  className?: string;
  asRegularUser?: boolean;
};

export default function SchoolsFilter({
  selectedSchoolsIds,
  onChange,
  showChips = false,
  label = 'Select Schools',
  className = 'w-60',
  asRegularUser = false,
}: SchoolsFilterProps) {
  const { data } = useQuery<FetchMySchoolsQuery, FetchMySchoolsQueryVariables>(QUERY_SCHOOL_CHILDREN, {
    variables: {
      asRegularUser,
    },
  });

  console.log({ data, asRegularUser });
  const [searchQuery, setSearchQuery] = useState('');

  const schools = data?.fetchMySchools;

  const filteredSchools = useMemo(() => {
    return schools?.filter((school) => school.attributes?.name?.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [schools, searchQuery]);

  const selectedSchools = useMemo(() => {
    return schools?.filter((school) => selectedSchoolsIds.includes(school.id!));
  }, [schools, selectedSchoolsIds]);

  const selectAllSchools = () => {
    const allSchoolIds = schools?.map((school) => school.id!).filter(Boolean) || [];
    onChange(allSchoolIds);
  };

  const unselectAllSchools = () => {
    onChange([]);
  };

  const handleRemoveSchool = (schoolId: string) => {
    onChange(selectedSchoolsIds.filter((id) => id !== schoolId));
  };

  return (
    <>
      <div className={className}>
        <Listbox value={selectedSchoolsIds} onChange={onChange} multiple>
          {({ open }) => (
            <>
              <Listbox.Button className="relative w-full cursor-pointer rounded-lg border border-[#CFD8DC] bg-white px-3 py-3 text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                <span className="block truncate text-sm">
                  {selectedSchoolsIds.length === 0
                    ? label
                    : selectedSchoolsIds.length === schools?.length
                      ? 'All schools'
                      : `${selectedSchoolsIds.length} school${selectedSchoolsIds.length > 1 ? 's' : ''}`}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </span>
              </Listbox.Button>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                <Listbox.Options
                  as="div"
                  className="absolute z-50 mt-1 max-h-60 w-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  <div className="border-b border-gray-200 p-2">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Search schools..."
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="flex border-b border-gray-200">
                    <button
                      type="button"
                      onClick={selectAllSchools}
                      className="hover:bg-primary-50 w-1/2 py-2 text-center text-sm text-primary-600">
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={unselectAllSchools}
                      className="hover:bg-primary-50 w-1/2 py-2 text-center text-sm text-primary-600">
                      Unselect All
                    </button>
                  </div>
                  {filteredSchools?.map((school) => (
                    <Listbox.Option
                      as="div"
                      key={school.id ?? `school-${Math.random()}`}
                      value={school.id || ''}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-3 pr-9 ${
                          active ? 'bg-primary-50 text-primary-900' : 'text-gray-900'
                        }`
                      }>
                      {({ selected }) => (
                        <div className="flex items-center gap-2">
                          <div
                            className={`flex h-4 min-w-4 items-center justify-center rounded border ${
                              selected ? 'border-primary-500 bg-primary-500' : 'border-gray-300'
                            }`}>
                            {selected && (
                              <svg className="h-3 w-3 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {school.attributes?.name}
                          </span>
                        </div>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Transition>
            </>
          )}
        </Listbox>
      </div>
      {showChips && selectedSchoolsIds.length > 0 && (
        <div className="mt-4 flex max-h-[100px] flex-wrap gap-2 overflow-y-auto">
          <AnimatePresence initial={false} mode="popLayout">
            {selectedSchools?.map((school) => (
              <motion.div
                key={school.id}
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8, y: -10 }}
                transition={{
                  duration: 0.2,
                  layout: {
                    duration: 0.2,
                  },
                }}
                className="flex items-center space-x-2 rounded-full border border-[#EEEEEE] bg-white px-3 py-1 text-sm">
                <span className="text-sh-xxs">{school.attributes?.name}</span>
                <button
                  type="button"
                  className="!ml-1 text-gray-500 hover:text-gray-700"
                  onClick={() => handleRemoveSchool(school.id!)}>
                  <XMarkIcon className="h-6 w-6 text-[#90A4AE]" />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </>
  );
}
